import { useCallback, useEffect, useRef, useState } from 'react'
import { createPortal } from 'react-dom'
import { useLocation, useNavigate } from 'react-router-dom'
import DifficultyBadge from '../components/difficultyBadge'
import { useAuth } from '../hooks'
import { api } from '../data/api'
import { gradeLabel } from '../utils/grading'
import { GRADING } from '../constants/config'

const DUMMY_HISTORY = [
  { id: 'h1',  examName: 'NISM Series VA – Mutual Fund Distributor', takenAt: '2026-06-10T09:15:00.000Z', mode: 'practice', difficulty: 'Intermediate', total: 10, score: 7,  grade: 'B',  passed: true,  correct: 7,  wrong: 3 },
  { id: 'h2',  examName: 'NISM Series VA – Mutual Fund Distributor', takenAt: '2026-06-15T14:30:00.000Z', mode: 'virtual',  difficulty: 'Intermediate', total: 10, score: 5,  grade: 'D',  passed: false, correct: 5,  wrong: 5 },
  { id: 'h3',  examName: 'NISM Series VA – Mutual Fund Distributor', takenAt: '2026-06-20T11:00:00.000Z', mode: 'practice', difficulty: 'Advanced',     total: 10, score: 8,  grade: 'A',  passed: true,  correct: 8,  wrong: 2 },
  { id: 'dh4', examName: 'NISM Series VA – Mutual Fund Distributor', takenAt: '2026-06-21T10:00:00.000Z', mode: 'practice', difficulty: 'Intermediate', total: 20, score: 12, grade: 'C',  passed: true,  correct: 12, wrong: 8 },
  { id: 'dh5', examName: 'NISM Series VA – Mutual Fund Distributor', takenAt: '2026-06-19T15:00:00.000Z', mode: 'virtual',  difficulty: 'Beginner',     total: 10, score: 3,  grade: 'F',  passed: false, correct: 3,  wrong: 7 },
  { id: 'dh6', examName: 'NISM Series VA – Mutual Fund Distributor', takenAt: '2026-06-17T09:30:00.000Z', mode: 'practice', difficulty: 'Advanced',     total: 30, score: 28, grade: 'A+', passed: true,  correct: 28, wrong: 2 },
  { id: 'dh7', examName: 'NISM Series VA – Mutual Fund Distributor', takenAt: '2026-06-14T13:00:00.000Z', mode: 'virtual',  difficulty: 'Intermediate', total: 20, score: 11, grade: 'D',  passed: false, correct: 11, wrong: 9 },
  { id: 'dh8', examName: 'NISM Series VA – Mutual Fund Distributor', takenAt: '2026-06-10T10:00:00.000Z', mode: 'practice', difficulty: 'Beginner',     total: 10, score: 8,  grade: 'B+', passed: true,  correct: 8,  wrong: 2 },
]

const GRADE_COLOR = {
  'A+': '#4ade80', 'A': '#4ade80', 'B+': '#86efac', 'B': '#86efac',
  'C': '#fbbf24', 'D': '#fb923c', 'F': '#f87171',
  'Excellent': '#4ade80', 'Good': '#86efac', 'Needs Work': '#fbbf24', 'Needs Significant Review': '#f87171',
}

const GRADE_LETTER = {
  'Excellent': 'A', 'Good': 'B', 'Needs Work': 'C', 'Needs Significant Review': 'D',
}

const PER_PAGE_OPTIONS = [5, 10, 50, 100]

function fmt(dateStr) {
  return new Date(dateStr).toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' })
}


export default function HistoryPage() {
  const location  = useLocation()
  const navigate  = useNavigate()
  const { user }  = useAuth()
  const viewUser  = location.state?.userId ? { id: location.state.userId, name: location.state.userName } : null
  const targetId  = viewUser?.id ?? user?._id ?? user?.id

  const [pageItems,  setPageItems]  = useState(null)
  const [meta,       setMeta]       = useState({ total: 0, pages: 1 })
  const [stats,      setStats]      = useState(null)
  const [tooltip,    setTooltip]    = useState(null)
  const [search,     setSearch]     = useState('')
  const [debouncedQ, setDebouncedQ] = useState('')
  const [page,       setPage]       = useState(1)
  const [perPage,    setPerPage]    = useState(5)
  const [loading,    setLoading]    = useState(true)
  const [voiding,    setVoiding]    = useState(null)
  const [toast,      setToast]      = useState('')
  const [trialSummary, setTrialSummary] = useState(null)
  const [orgTrialSummary, setOrgTrialSummary] = useState(null)

  // Debounce search input 400ms
  useEffect(() => {
    const t = setTimeout(() => setDebouncedQ(search), 400)
    return () => clearTimeout(t)
  }, [search])

  // Reset to page 1 when search or perPage changes
  useEffect(() => { setPage(1) }, [debouncedQ, perPage])

  const mapAttempt = useCallback(a => {
    const ph = a.userId?.phone
    const phone = ph?.number ? `${ph.countryCode ?? ''} ${ph.number}`.trim() : null
    return {
      id:            a._id,
      examName:      a.examId?.name ?? '—',
      takenAt:       a.submittedAt,
      difficulty:    a.examId?.difficulty ?? null,
      total:         a.totalQ,
      score:         a.score ?? 0,
      correct:       a.correctQ,
      wrong:         (a.totalQ ?? 0) - (a.correctQ ?? 0),
      passed:        (a.score ?? 0) >= GRADING.PASS_CUTOFF_PCT,
      grade:         gradeLabel(a.score ?? 0),
      retakeGranted: a.retakeGranted,
      flags:         a.flags,
      userName:      a.userId?.name  ?? null,
      userEmail:     a.userId?.email ?? null,
      userPhone:     phone,
    }
  }, [])

  useEffect(() => {
    if (!targetId) return
    setLoading(true)
    const params = new URLSearchParams({ page, limit: perPage })
    if (debouncedQ) params.set('q', debouncedQ)

    const orgId    = user?._id ?? user?.id
    const isAdmin  = user?.role === 'organisation' || user?.role === 'superadmin'
    const endpoint = (viewUser || isAdmin)
      ? `/api/users/history/${orgId}?${viewUser ? `userId=${viewUser.id}&` : ''}${params}`
      : `/api/users/history?${params}`

    api.get(endpoint)
      .then(data => {
        setPageItems((data.attempts ?? []).map(mapAttempt))
        setMeta({ total: data.total ?? 0, pages: data.pages ?? 1 })
        if (data.stats) setStats(data.stats)
      })
      .catch(() => setPageItems([]))
      .finally(() => setLoading(false))
  }, [targetId, page, perPage, debouncedQ])

  // Trials never create an ExamAttempt (graded client-side) — this is the only
  // place the per-level attempt count lives, so it's a separate fetch. Covers
  // two cases with the same endpoint: an org looking at one specific
  // examinee (viewUser), or an examinee looking at their own history.
  const selfId = (!viewUser && user?.role === 'user') ? (user?._id ?? user?.id) : null
  const trialSubjectId = viewUser?.id ?? selfId
  useEffect(() => {
    if (!trialSubjectId) { setTrialSummary(null); return }
    api.get(`/api/users/${trialSubjectId}/trial-summary`)
      .then(setTrialSummary)
      .catch(() => setTrialSummary([]))
  }, [trialSubjectId])

  // Org-wide equivalent — every examinee, every exam they've Trialed, shown
  // only on the all-examinees view (not when already drilled into one person).
  useEffect(() => {
    const orgId   = user?._id ?? user?.id
    const isAdmin = user?.role === 'organisation' || user?.role === 'superadmin'
    if (viewUser || !isAdmin || !orgId) { setOrgTrialSummary(null); return }
    api.get(`/api/users/trial-summary/${orgId}`)
      .then(setOrgTrialSummary)
      .catch(() => setOrgTrialSummary([]))
  }, [viewUser, user?._id, user?.id, user?.role])

  async function handleGrantRetake(id) {
    setVoiding(id)
    try {
      await api.patch(`/api/users/attempts/${id}/void`, {})
      setPageItems(prev => prev.map(e => e.id === id ? { ...e, retakeGranted: true } : e))
      setToast('Retake granted successfully.')
      setTimeout(() => setToast(''), 3000)
    } catch {
      setToast('Failed to grant retake.')
      setTimeout(() => setToast(''), 3000)
    } finally {
      setVoiding(null)
    }
  }

  const MALPRACTICE = { tabSwitch: true, screenShare: true }

  const SUBMIT_TYPE = {
    manual:      { label: 'Submitted',    sub: 'By examinee',    color: '#4ade80' },
    timer:       { label: 'Time Expired', sub: 'Auto-submitted', color: '#fbbf24' },
    tabSwitch:   { label: 'Tab Switch',   sub: 'Auto-submitted', color: '#f87171' },
    screenShare: { label: 'Screen Share', sub: 'Auto-submitted', color: '#fb923c' },
    tabClosed:   { label: 'Tab Closed',   sub: 'Auto-submitted', color: '#a78bfa' },
  }

  function getSubmitType(flags) {
    if (!flags) return null
    if (!flags.autoSubmitted) return 'manual'
    return flags.submitReason ?? 'timer'
  }

  if (loading && pageItems === null) return (
    <div className="px-8 sm:px-40 py-8 sm:py-12 text-sm" style={{ color: 'var(--text-3)' }}>Loading…</div>
  )

  const isAdmin    = user?.role === 'organisation' || user?.role === 'superadmin'
  const showUser   = isAdmin && !viewUser   // show examinee column when org admin sees all history
  const items      = pageItems ?? []
  const totalPages = Math.max(1, meta.pages)
  const safePage   = Math.min(page, totalPages)

  function onSearch(v) { setSearch(v) }
  function onPerPage(v) { setPerPage(Number(v)) }

  function showNameTooltip(e, name) {
    const p = e.currentTarget.querySelector('p')
    if (!p || p.scrollWidth <= p.clientWidth) return
    setTooltip({ name, rect: e.currentTarget.getBoundingClientRect() })
  }

  return (
    <div className="px-8 sm:px-40 py-8 sm:py-12">

      {/* Toast */}
      {toast && (
        <div className="fixed top-16 left-1/2 -translate-x-1/2 z-50 flex items-center gap-2 px-4 py-3 rounded-xl border text-sm shadow-2xl animate-fade-in whitespace-nowrap"
             style={{ background: toast.startsWith('Failed') ? '#3b0d0d' : '#0d2b1f', borderColor: toast.startsWith('Failed') ? 'rgba(248,113,113,0.4)' : 'rgba(52,211,153,0.4)', color: toast.startsWith('Failed') ? '#f87171' : '#34d399' }}>
          {toast.startsWith('Failed') ? '✗' : '✓'} {toast}
        </div>
      )}

      {/* Page header */}
      <div className="mb-8">
        {viewUser && (
          <button onClick={() => navigate('/admin', { state: { tab: 'users' } })}
            className="text-xs font-medium mb-4 flex items-center gap-1 transition"
            style={{ color: 'var(--text-3)' }}
            onMouseEnter={e => e.currentTarget.style.color = 'var(--text-1)'}
            onMouseLeave={e => e.currentTarget.style.color = 'var(--text-3)'}
          >← Back to Users</button>
        )}
        <p className="text-xs font-semibold uppercase tracking-widest mb-2" style={{ color: 'var(--brand)' }}>
          {viewUser ? `${viewUser.name}'s Progress` : 'Your Progress'}
        </p>
        <h1 className="text-3xl sm:text-4xl font-black tracking-tight mb-2" style={{ color: 'var(--text-1)' }}>Exam History</h1>
        <p className="text-sm" style={{ color: 'var(--text-3)' }}>
          {viewUser ? `Exam history for ${viewUser.name}` : 'A record of all your submitted exams'}
        </p>
      </div>

      {/* Stats strip — Trial Attempts joins the row (org viewing one examinee
          only), always shown once loaded, 0 included, rather than hiding
          when there's nothing to report. Column count adapts so 5 tiles
          stay readable at every width instead of leaving an orphaned 5th. */}
      {(() => {
        const sumLevels = rows => rows.reduce((sum, { levels }) => sum + Object.values(levels).reduce((s, n) => s + n, 0), 0)
        // Per-examinee view uses trialSummary; the org-wide view (no examinee
        // selected) uses orgTrialSummary instead — same tile either way, just
        // a different total and a different destination page.
        const trialTotal = (viewUser || selfId)
          ? (trialSummary ? sumLevels(trialSummary) : null)
          : (orgTrialSummary ? sumLevels(orgTrialSummary) : null)
        const trialTile = (viewUser || showUser || selfId) ? [{
          v: trialTotal ?? '—', l: 'Trial Attempts', c: 'var(--brand)',
          onClick: () => navigate('/trial-attempts', viewUser ? { state: { userId: viewUser.id, userName: viewUser.name } } : undefined),
        }] : []
        const tiles = [
          { v: stats?.totalExams ?? '—', l: 'Total Exams', c: 'var(--text-1)' },
          { v: stats?.passed     ?? '—', l: 'Passed',      c: '#4ade80' },
          { v: stats?.failed     ?? '—', l: 'Failed',      c: '#f87171' },
          { v: stats ? `${stats.avgScore}%` : '—', l: 'Avg Score', c: 'var(--brand)' },
          ...trialTile,
        ]
        return (
          <div className={`grid grid-cols-2 ${tiles.length === 5 ? 'sm:grid-cols-3 lg:grid-cols-5' : 'sm:grid-cols-4'} gap-4 mb-8`}>
            {tiles.map(({ v, l, c, onClick }) => {
              const Tag = onClick ? 'button' : 'div'
              return (
                <Tag key={l} onClick={onClick} style={onClick ? { cursor: 'pointer' } : undefined}
                  className={`card p-5 text-center${onClick ? ' transition-colors' : ''}`}
                  onMouseEnter={onClick ? e => e.currentTarget.style.background = 'var(--surface-2)' : undefined}
                  onMouseLeave={onClick ? e => e.currentTarget.style.background = '' : undefined}>
                  <div className="text-3xl font-black mb-1" style={{ color: c }}>{v}</div>
                  <div className="text-xs font-medium flex items-center justify-center gap-1" style={{ color: 'var(--text-3)' }}>
                    {l}
                    {onClick && (
                      <svg className="w-3 h-3 -rotate-90" viewBox="0 0 12 12" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round">
                        <path d="M2 4l4 4 4-4" />
                      </svg>
                    )}
                  </div>
                </Tag>
              )
            })}
          </div>
        )
      })()}

      {/* Search bar */}
      <div className="mb-4 flex items-center gap-3">
        <div className="relative w-56">
          <span className="absolute left-3 top-1/2 -translate-y-1/2 text-sm" style={{ color: 'var(--text-3)' }}>🔍</span>
          <input
            type="text"
            value={search}
            onChange={e => onSearch(e.target.value)}
            placeholder="Search..."
            className="w-full pl-9 pr-4 py-2.5 rounded-xl text-sm outline-none transition-all"
            style={{
              background: 'var(--surface)',
              border: '1px solid var(--border)',
              color: 'var(--text-1)',
            }}
            onFocus={e => e.currentTarget.style.borderColor = 'var(--brand)'}
            onBlur={e  => e.currentTarget.style.borderColor = 'var(--border)'}
          />
          {search && (
            <button onClick={() => onSearch('')}
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-xs"
                    style={{ color: 'var(--text-3)' }}>✕</button>
          )}
        </div>
        <span className="text-xs" style={{ color: 'var(--text-3)' }}>
          {loading ? '…' : `${meta.total} result${meta.total !== 1 ? 's' : ''}`}
        </span>

        <div className="flex items-center gap-2 ml-auto">
          <span className="text-xs" style={{ color: 'var(--text-3)' }}>Rows per page</span>
          <select value={perPage} onChange={e => onPerPage(e.target.value)}
                  className="text-xs rounded-lg px-2 py-1.5 outline-none cursor-pointer"
                  style={{ background: 'var(--surface)', border: '1px solid var(--border)', color: 'var(--text-1)' }}>
            {PER_PAGE_OPTIONS.map(n => <option key={n} value={n}>{n}</option>)}
          </select>
        </div>
      </div>

      {/* History table */}
      <div className="card overflow-hidden">
        {/* Header */}
        <div className="hidden sm:grid gap-4 px-6 py-3.5 text-xs font-bold uppercase tracking-widest border-b"
             style={{
               gridTemplateColumns: showUser ? '2fr 1.8fr 1.5fr 1.2fr 0.8fr 1.3fr 1.6fr 0.6fr' : '2fr 1.5fr 1.2fr 0.8fr 1.3fr 1.6fr 0.6fr',
               color: 'var(--text-3)',
               borderColor: 'var(--border)',
               background: 'var(--surface-2)',
             }}>
          <span>Exam Name</span>
          {showUser && <span>Examinee</span>}
          <span>Date</span>
          {/* <span>Difficulty</span> */}
          <span>Questions</span>
          <span>Score</span>
          <span className="text-center">Result</span>
          <span className="pl-6">Submission</span>
          <span className="text-center">Actions</span>
        </div>

        {items.length === 0 ? (
          <div className="py-16 text-center">
            {!debouncedQ && stats?.totalExams === 0 ? (
              <>
                <p className="text-3xl mb-3">📋</p>
                <p className="text-sm font-medium" style={{ color: 'var(--text-2)' }}>
                  {viewUser ? `${viewUser.name} hasn't taken any exams yet.` : 'No exam history yet.'}
                </p>
              </>
            ) : (
              <>
                <p className="text-3xl mb-3">🔍</p>
                <p className="text-sm font-medium" style={{ color: 'var(--text-2)' }}>No results found</p>
                <p className="text-xs mt-1" style={{ color: 'var(--text-3)' }}>Try a different search term</p>
              </>
            )}
          </div>
        ) : (
          <>
            {/* Desktop rows */}
            {items.map((e, i) => {
              const dateStr    = e.date ?? e.takenAt
              const pct        = Math.round(e.score ?? 0)
              const gradeColor = GRADE_COLOR[e.grade] ?? (e.passed ? '#4ade80' : '#f87171')
              const passColor  = e.passed ? '#4ade80' : '#f87171'
              const passBg     = e.passed ? 'rgba(74,222,128,0.08)' : 'rgba(248,113,113,0.08)'
              const passBorder = e.passed ? 'rgba(74,222,128,0.25)' : 'rgba(248,113,113,0.25)'
              const hasDetail  = !!e.takenAt
              return (
                <div key={e.id}
                     className="hidden sm:grid gap-4 px-6 py-4 items-center border-b last:border-0 transition-colors cursor-default"
                     style={{ gridTemplateColumns: showUser ? '2fr 1.8fr 1.5fr 1.2fr 0.8fr 1.3fr 1.6fr 0.6fr' : '2fr 1.5fr 1.2fr 0.8fr 1.3fr 1.6fr 0.6fr', borderColor: 'var(--border)' }}
                     onMouseEnter={ev => ev.currentTarget.style.background = 'var(--surface-2)'}
                     onMouseLeave={ev => ev.currentTarget.style.background = 'transparent'}
                >
                  <div className="min-w-0"
                       onMouseEnter={ev => showNameTooltip(ev, e.examName)}
                       onMouseLeave={() => setTooltip(null)}>
                    <p className="text-sm font-semibold truncate" style={{ color: 'var(--text-1)' }}>
                      {e.examName ?? '—'}
                    </p>
                  </div>
                  {showUser && (
                    <div className="min-w-0">
                      <p className="text-sm font-semibold truncate" style={{ color: 'var(--text-1)' }}>{e.userName ?? '—'}</p>
                      {e.userEmail && <p className="text-xs truncate mt-0.5" style={{ color: 'var(--text-3)' }}>{e.userEmail}</p>}
                      {e.userPhone && <p className="text-xs mt-0.5" style={{ color: 'var(--text-3)' }}>{e.userPhone}</p>}
                    </div>
                  )}
                  <div>
                    <p className="text-sm font-semibold" style={{ color: 'var(--text-1)' }}>{fmt(dateStr)}</p>
                  </div>
                  {/* <div><DifficultyBadge level={e.difficulty} /></div> */}
                  <div>
                    <p className="text-sm font-semibold" style={{ color: 'var(--text-1)' }}>{e.total}</p>
                  </div>
                  <div><span className="text-sm font-bold" style={{ color: gradeColor }}>{pct}%</span></div>
                  <div className="flex justify-center">
                    <span className="text-xs font-bold px-3 py-1 rounded-full"
                          style={{ background: passBg, color: passColor, border: `1px solid ${passBorder}` }}>
                      {e.passed ? 'PASSED' : 'FAILED'}
                    </span>
                  </div>
                  {/* Submission column */}
                  <div className="pl-6">
                    {(() => {
                      const key  = getSubmitType(e.flags)
                      const info = SUBMIT_TYPE[key]
                      if (!info) return <span className="text-xs" style={{ color: 'var(--text-3)' }}>—</span>
                      return (
                        <div className="space-y-1">
                          <span className="text-xs font-semibold" style={{ color: info.color }}>{info.label}</span>
                          <p className="text-[0.65rem]" style={{ color: 'var(--text-3)' }}>{info.sub}</p>
                          {e.retakeGranted && (
                            <div className="flex items-center gap-1 mt-1">
                              <svg viewBox="0 0 16 16" className="w-3 h-3 flex-shrink-0" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" style={{ color: 'var(--brand)' }}>
                                <path d="M13 2v4h-4"/><path d="M13 6A6 6 0 1 1 9.5 2.5"/>
                              </svg>
                              <span className="text-[0.65rem] font-semibold" style={{ color: 'var(--brand)' }}>Retake Granted</span>
                            </div>
                          )}
                        </div>
                      )
                    })()}
                  </div>
                  {/* Actions column */}
                  <div className="flex items-center justify-center gap-1.5">
                    {/* Grant retake — org admin only, malpractice only */}
                    {isAdmin && e.flags?.autoSubmitted && MALPRACTICE[e.flags?.submitReason] && !e.retakeGranted && (
                      <button
                        onClick={() => handleGrantRetake(e.id)}
                        disabled={voiding === e.id}
                        className="w-7 h-7 rounded-lg flex items-center justify-center transition-all disabled:opacity-40"
                        style={{ color: 'var(--brand)', border: '1px solid rgba(14,165,233,0.3)', background: 'rgba(14,165,233,0.08)' }}
                        title="Grant Retake"
                        onMouseEnter={ev => { ev.currentTarget.style.background = 'rgba(14,165,233,0.18)'; ev.currentTarget.style.borderColor = 'var(--brand)' }}
                        onMouseLeave={ev => { ev.currentTarget.style.background = 'rgba(14,165,233,0.08)'; ev.currentTarget.style.borderColor = 'rgba(14,165,233,0.3)' }}
                      >
                        {voiding === e.id
                          ? <span className="text-xs">…</span>
                          : <svg viewBox="0 0 16 16" className="w-4 h-4" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
                              <path d="M13 2v4h-4"/><path d="M13 6A6 6 0 1 1 9.5 2.5"/>
                            </svg>
                        }
                      </button>
                    )}
                    {hasDetail && (
                      <button onClick={() => navigate(`/result/${e.id}`)}
                        className="w-7 h-7 rounded-lg flex items-center justify-center transition-all"
                        style={{ color: 'var(--text-3)', border: '1px solid var(--border)' }}
                        title="Review answers"
                        onMouseEnter={ev => { ev.currentTarget.style.borderColor = 'var(--brand)'; ev.currentTarget.style.color = 'var(--brand)' }}
                        onMouseLeave={ev => { ev.currentTarget.style.borderColor = 'var(--border)'; ev.currentTarget.style.color = 'var(--text-3)' }}
                      >
                        <svg viewBox="0 0 16 16" className="w-4 h-4" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
                          <path d="M1 8s2.5-5 7-5 7 5 7 5-2.5 5-7 5-7-5-7-5z"/><circle cx="8" cy="8" r="2"/>
                        </svg>
                      </button>
                    )}
                  </div>
                </div>
              )
            })}

            {/* Mobile cards */}
            {items.map((e) => {
              const dateStr    = e.date ?? e.takenAt
              const pct        = Math.round(e.score ?? 0)
              const gradeColor = GRADE_COLOR[e.grade] ?? (e.passed ? '#4ade80' : '#f87171')
              const hasDetail  = !!e.takenAt
              const passColor  = e.passed ? '#4ade80' : '#f87171'
              const passBg     = e.passed ? 'rgba(74,222,128,0.08)' : 'rgba(248,113,113,0.08)'
              const passBorder = e.passed ? 'rgba(74,222,128,0.25)' : 'rgba(248,113,113,0.25)'
              return (
                <div key={`m-${e.id}`} className="sm:hidden px-4 py-4 border-b last:border-0"
                     style={{ borderColor: 'var(--border)' }}>
                  <div className="flex items-start justify-between mb-3">
                    <div className="min-w-0 flex-1 mr-3">
                      {e.examName && <p className="text-sm font-semibold truncate mb-0.5" style={{ color: 'var(--text-1)' }}>{e.examName}</p>}
                      {showUser && e.userName && (
                        <p className="text-xs font-medium mb-0.5" style={{ color: 'var(--text-2)' }}>
                          {e.userName}{e.userEmail ? ` · ${e.userEmail}` : ''}{e.userPhone ? ` · ${e.userPhone}` : ''}
                        </p>
                      )}
                      <p className="text-xs" style={{ color: 'var(--text-3)' }}>{fmt(dateStr)}</p>
                      <p className="text-xs mt-0.5" style={{ color: 'var(--text-3)' }}>{e.total} Questions</p>
                    </div>
                    <div className="flex items-center gap-2">
                      <span className="text-xs font-bold px-2.5 py-1 rounded-full"
                            style={{ background: passBg, color: passColor, border: `1px solid ${passBorder}` }}>
                        {e.passed ? 'PASSED' : 'FAILED'}
                      </span>
                      {hasDetail && (
                        <button onClick={() => navigate(`/result/${e.id}`)}
                          className="w-7 h-7 rounded-lg flex items-center justify-center flex-shrink-0"
                          style={{ color: 'var(--text-3)', border: '1px solid var(--border)' }}>
                          <svg viewBox="0 0 16 16" className="w-3.5 h-3.5" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
                            <path d="M1 8s2.5-5 7-5 7 5 7 5-2.5 5-7 5-7-5-7-5z"/><circle cx="8" cy="8" r="2"/>
                          </svg>
                        </button>
                      )}
                    </div>
                  </div>
                  <span className="text-sm font-bold" style={{ color: gradeColor }}>{pct}%</span>
                  {e.correct != null && (
                    <div className="flex items-center gap-4 mt-2 text-xs" style={{ color: 'var(--text-3)' }}>
                      <span><span className="text-emerald-400 font-bold">{e.correct}</span> correct</span>
                      <span><span className="text-red-400 font-bold">{e.wrong}</span> wrong</span>
                    </div>
                  )}
                  <div className="mt-1 flex items-center gap-2 flex-wrap">
                    <DifficultyBadge level={e.difficulty} />
                    {(() => {
                      const key  = getSubmitType(e.flags)
                      const info = SUBMIT_TYPE[key]
                      if (!info) return null
                      return (
                        <>
                          <span className="text-[0.65rem] font-semibold" style={{ color: info.color }}>{info.label}</span>
                          {e.retakeGranted && (
                            <span className="flex items-center gap-1">
                              <svg viewBox="0 0 16 16" className="w-3 h-3" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" style={{ color: 'var(--brand)' }}>
                                <path d="M13 2v4h-4"/><path d="M13 6A6 6 0 1 1 9.5 2.5"/>
                              </svg>
                              <span className="text-[0.65rem] font-semibold" style={{ color: 'var(--brand)' }}>Retake Granted</span>
                            </span>
                          )}
                        </>
                      )
                    })()}
                    {isAdmin && e.flags?.autoSubmitted && MALPRACTICE[e.flags?.submitReason] && !e.retakeGranted && (
                      <button
                        onClick={() => handleGrantRetake(e.id)}
                        disabled={voiding === e.id}
                        className="text-[0.6rem] font-semibold px-2 py-0.5 rounded-lg transition disabled:opacity-40"
                        style={{ background: 'rgba(14,165,233,0.1)', color: 'var(--brand)', border: '1px solid rgba(14,165,233,0.3)' }}>
                        {voiding === e.id ? '…' : 'Grant Retake'}
                      </button>
                    )}
                  </div>
                </div>
              )
            })}
          </>
        )}
      </div>

      {/* Pagination */}
      {tooltip && createPortal(
        <div style={{
          position: 'fixed',
          top: tooltip.rect.bottom + 6,
          left: tooltip.rect.left,
          zIndex: 9999,
          maxWidth: '18rem',
          padding: '0.4rem 0.75rem',
          borderRadius: '0.6rem',
          background: 'var(--surface)',
          border: '1px solid var(--border)',
          boxShadow: '0 8px 24px rgba(0,0,0,0.2)',
          pointerEvents: 'none',
        }}>
          <p className="text-xs font-semibold leading-snug" style={{ color: 'var(--text-1)' }}>{tooltip.name}</p>
        </div>,
        document.body
      )}

      {totalPages > 1 && (
        <div className="flex items-center justify-between mt-5">
          <p className="text-xs" style={{ color: 'var(--text-3)' }}>
            Showing {(safePage - 1) * perPage + 1}–{Math.min(safePage * perPage, meta.total)} of {meta.total}
          </p>
          <div className="flex items-center gap-1">
            <button
              onClick={() => setPage(p => Math.max(1, p - 1))}
              disabled={safePage === 1}
              className="px-3 py-1.5 rounded-lg text-xs font-medium transition-all disabled:opacity-30"
              style={{ background: 'var(--surface)', border: '1px solid var(--border)', color: 'var(--text-2)' }}
            >← Prev</button>

            {Array.from({ length: totalPages }, (_, i) => i + 1).map(n => (
              <button key={n} onClick={() => setPage(n)}
                      className="w-8 h-8 rounded-lg text-xs font-semibold transition-all"
                      style={n === safePage
                        ? { background: 'var(--brand)', color: '#fff' }
                        : { background: 'var(--surface)', border: '1px solid var(--border)', color: 'var(--text-2)' }
                      }>
                {n}
              </button>
            ))}

            <button
              onClick={() => setPage(p => Math.min(totalPages, p + 1))}
              disabled={safePage === totalPages}
              className="px-3 py-1.5 rounded-lg text-xs font-medium transition-all disabled:opacity-30"
              style={{ background: 'var(--surface)', border: '1px solid var(--border)', color: 'var(--text-2)' }}
            >Next →</button>
          </div>
        </div>
      )}

    </div>
  )
}
